import React, { useEffect, useMemo, useState } from "react";
import {
    Shield, Globe, Server, HardDrive, Mail, Database, Layers, Archive,
    Eye, Plus, Pencil, Loader2, Search, AlertTriangle, Lock, Activity,
    RotateCcw, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getJSON, postJSON, delJSON } from "../../lib/api";
import { MagneticButton } from "../MagneticButton";
import { ImportantIPForm } from "./ImportantIPForm";

const ROLE_ICON = {
    dns: Globe, dc: Shield, gateway: ArrowUpRight, file_server: Server,
    mail: Mail, web: Globe, api: Activity, nas: HardDrive, vpn: Lock,
    monitoring: Eye, database: Database, backup: Archive, other: Layers,
};

const CRITICALITY_TONES = {
    critical: "border-accent-red/40 text-accent-red bg-accent-red/[0.08]",
    high: "border-accent-amber/40 text-accent-amber bg-accent-amber/[0.08]",
    medium: "border-accent-cyan/30 text-accent-cyan bg-accent-cyan/[0.06]",
    low: "border-white/[0.1] text-text-muted",
};

const CRITICALITY_ORDER = ["critical", "high", "medium", "low"];

const ROLE_LABEL = {
    dns: "DNS", dc: "Domain Ctrl", gateway: "Gateway", file_server: "File Server",
    mail: "Mail", web: "Web", api: "API", nas: "NAS", vpn: "VPN",
    monitoring: "Monitoring", database: "Database", backup: "Backup", other: "Other",
};

export const ImportantIPsTab = ({ client, onChanged }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subnets, setSubnets] = useState([]);
    const [hardware, setHardware] = useState([]);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const [roleFilter, setRoleFilter] = useState([]); // array of role ids
    const [critFilter, setCritFilter] = useState([]); // array of criticalities
    const [statusFilter, setStatusFilter] = useState("active"); // active|archived|all
    const [q, setQ] = useState("");

    const reload = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("status", statusFilter);
            if (roleFilter.length) params.set("role", roleFilter.join(","));
            if (critFilter.length) params.set("criticality", critFilter.join(","));
            if (q.trim().length >= 2) params.set("q", q.trim());
            params.set("limit", "100");
            const data = await getJSON(`/ipam/clients/${client.id}/important-ips?${params}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // load subnets + hardware once so the form can offer them as dropdowns
        Promise.all([
            getJSON(`/ipam/clients/${client.id}/subnets?limit=100`).catch(() => ({ items: [] })),
            getJSON(`/ipam/clients/${client.id}/hardware?limit=100&status=active`).catch(() => ({ items: [] })),
        ]).then(([s, h]) => {
            setSubnets(s.items || []);
            setHardware(h.items || []);
        });
    }, [client.id]);

    useEffect(() => {
        const t = setTimeout(reload, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [client.id, statusFilter, roleFilter.join(","), critFilter.join(","), q]);

    const grouped = useMemo(() => {
        // sort by criticality, then ip
        return [...items].sort((a, b) => {
            const ai = CRITICALITY_ORDER.indexOf(a.criticality);
            const bi = CRITICALITY_ORDER.indexOf(b.criticality);
            if (ai !== bi) return ai - bi;
            return (a.ip_address || "").localeCompare(b.ip_address || "", undefined, { numeric: true });
        });
    }, [items]);

    const toggleRole = (r) => setRoleFilter((s) => s.includes(r) ? s.filter((x) => x !== r) : [...s, r]);
    const toggleCrit = (c) => setCritFilter((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);

    const archive = async (ip) => {
        try {
            await delJSON(`/ipam/important-ips/${ip.id}`);
            toast.success("Archived");
            reload();
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.message || "Failed");
        }
    };
    const restore = async (ip) => {
        try {
            await postJSON(`/ipam/important-ips/${ip.id}/restore`, {});
            toast.success("Restored");
            reload();
            onChanged && onChanged();
        } catch (e) {
            toast.error(e.message || "Failed");
        }
    };

    const subnetMap = useMemo(() => Object.fromEntries(subnets.map((s) => [s.id, s])), [subnets]);
    const hardwareMap = useMemo(() => Object.fromEntries(hardware.map((h) => [h.id, h])), [hardware]);

    return (
        <div data-testid="ips-tab">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="relative grow min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-dim" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, IP, FQDN…"
                        className="w-full pl-9 pr-3 py-2 rounded-md bg-surface/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                        data-testid="ips-search"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 rounded-md bg-surface/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                    data-testid="ips-status-filter"
                >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                    <option value="all">All</option>
                </select>
                <MagneticButton
                    type="button"
                    onClick={() => setAdding(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan"
                    ariaLabel="Add important IP"
                    data-testid="ips-add-btn"
                >
                    <Plus className="w-3.5 h-3.5" /> Add IP
                </MagneticButton>
            </div>

            {/* Filter chips: criticality + role */}
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ips-filter-crits">
                <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-dim mr-1 self-center">Criticality</span>
                {CRITICALITY_ORDER.map((c) => (
                    <button
                        key={c}
                        type="button"
                        onClick={() => toggleCrit(c)}
                        className={`px-2.5 py-1 rounded text-[11.5px] font-medium border transition-colors ${
                            critFilter.includes(c)
                                ? CRITICALITY_TONES[c]
                                : "border-white/[0.06] text-text-muted hover:border-accent-cyan/30"
                        }`}
                        data-testid={`ips-filter-crit-${c}`}
                    >
                        {c}
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-5" data-testid="ips-filter-roles">
                <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-dim mr-1 self-center">Role</span>
                {Object.keys(ROLE_LABEL).map((r) => (
                    <button
                        key={r}
                        type="button"
                        onClick={() => toggleRole(r)}
                        className={`px-2.5 py-1 rounded text-[11.5px] font-medium border transition-colors ${
                            roleFilter.includes(r)
                                ? "bg-accent-cyan/[0.12] border-accent-cyan/40 text-accent-cyan"
                                : "border-white/[0.06] text-text-muted hover:border-accent-cyan/30"
                        }`}
                        data-testid={`ips-filter-role-${r}`}
                    >
                        {ROLE_LABEL[r]}
                    </button>
                ))}
            </div>

            {/* Body */}
            {loading ? (
                <div className="py-10 text-center text-text-muted flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            ) : grouped.length === 0 ? (
                <div
                    className="py-12 text-center border border-dashed border-white/[0.06] rounded-lg"
                    data-testid="ips-empty"
                >
                    <AlertTriangle className="w-5 h-5 text-text-dim mx-auto" />
                    <p className="mt-3 text-[13px] text-text-muted">No important IPs yet</p>
                    <p className="mt-1 text-[11.5px] text-text-dim">
                        Add servers, gateways, DCs you care about — they'll feed the future CVE scan.
                    </p>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block rounded-lg border border-white/[0.05] overflow-hidden" data-testid="ips-table">
                        <table className="w-full text-[12.5px]">
                            <thead className="bg-elevated/60 text-text-dim">
                                <tr className="text-left">
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">Crit</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">Name</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">IP</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">Role</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">Subnet</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">Hardware</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em]">Port</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouped.map((ip) => {
                                    const Icon = ROLE_ICON[ip.role] || Layers;
                                    const sub = subnetMap[ip.subnet_id];
                                    const hw = hardwareMap[ip.hardware_id];
                                    return (
                                        <tr key={ip.id} className="border-t border-white/[0.05] hover:bg-elevated/30" data-testid={`ips-row-${ip.id}`}>
                                            <td className="px-3 py-2.5">
                                                <span
                                                    className={`inline-block px-2 py-0.5 rounded text-[10.5px] font-mono uppercase border ${CRITICALITY_TONES[ip.criticality] || ""}`}
                                                >
                                                    {ip.criticality}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-text-primary">
                                                {ip.name}
                                                {ip.fqdn && (
                                                    <div className="text-[10.5px] font-mono text-text-dim mt-0.5">{ip.fqdn}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 font-mono text-text-primary">{ip.ip_address}</td>
                                            <td className="px-3 py-2.5">
                                                <span className="inline-flex items-center gap-1 text-text-muted">
                                                    <Icon className="w-3 h-3" /> {ROLE_LABEL[ip.role] || ip.role}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-text-muted font-mono text-[11.5px]">
                                                {sub ? sub.cidr : <span className="text-text-dim">—</span>}
                                            </td>
                                            <td className="px-3 py-2.5 text-text-muted text-[11.5px]">
                                                {hw ? (
                                                    <span>{hw.vendor} {hw.model}{hw.hostname ? ` · ${hw.hostname}` : ""}</span>
                                                ) : (
                                                    <span className="text-text-dim">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 font-mono text-text-muted text-[11.5px]">
                                                {ip.service_port ? `${ip.service_port}/${ip.protocol}` : <span className="text-text-dim">—</span>}
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditing(ip)}
                                                    className="p-1.5 rounded text-text-muted hover:text-accent-cyan"
                                                    aria-label="Edit"
                                                    data-testid={`ips-edit-${ip.id}`}
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                {ip.status === "archived" ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => restore(ip)}
                                                        className="p-1.5 rounded text-text-muted hover:text-accent-mint"
                                                        aria-label="Restore"
                                                        data-testid={`ips-restore-${ip.id}`}
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => archive(ip)}
                                                        className="p-1.5 rounded text-text-muted hover:text-accent-amber"
                                                        aria-label="Archive"
                                                        data-testid={`ips-archive-${ip.id}`}
                                                    >
                                                        <Archive className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-2.5" data-testid="ips-card-list">
                        {grouped.map((ip) => {
                            const Icon = ROLE_ICON[ip.role] || Layers;
                            const sub = subnetMap[ip.subnet_id];
                            const hw = hardwareMap[ip.hardware_id];
                            return (
                                <div key={ip.id} className="p-3 rounded-md border border-white/[0.06] bg-surface/40" data-testid={`ips-card-${ip.id}`}>
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <span
                                            className={`inline-block px-2 py-0.5 rounded text-[10.5px] font-mono uppercase border ${CRITICALITY_TONES[ip.criticality] || ""}`}
                                        >
                                            {ip.criticality}
                                        </span>
                                        <Icon className="w-3.5 h-3.5 text-text-muted" />
                                        <span className="text-text-primary text-[13px] font-medium">{ip.name}</span>
                                    </div>
                                    <div className="font-mono text-[12.5px] text-text-primary">{ip.ip_address}</div>
                                    {ip.fqdn && <div className="font-mono text-[11px] text-text-dim">{ip.fqdn}</div>}
                                    <div className="mt-2 text-[11.5px] text-text-muted flex flex-wrap gap-x-3 gap-y-1">
                                        <span>{ROLE_LABEL[ip.role] || ip.role}</span>
                                        {sub && <span className="font-mono">{sub.cidr}</span>}
                                        {ip.service_port && <span className="font-mono">{ip.service_port}/{ip.protocol}</span>}
                                    </div>
                                    {hw && (
                                        <div className="mt-1 text-[11px] text-text-dim">
                                            HW: {hw.vendor} {hw.model}
                                        </div>
                                    )}
                                    <div className="mt-2 flex items-center justify-end gap-1">
                                        <button onClick={() => setEditing(ip)} className="p-1.5 text-text-muted hover:text-accent-cyan" aria-label="Edit">
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        {ip.status === "archived" ? (
                                            <button onClick={() => restore(ip)} className="p-1.5 text-text-muted hover:text-accent-mint" aria-label="Restore">
                                                <RotateCcw className="w-3.5 h-3.5" />
                                            </button>
                                        ) : (
                                            <button onClick={() => archive(ip)} className="p-1.5 text-text-muted hover:text-accent-amber" aria-label="Archive">
                                                <Archive className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {(adding || editing) && (
                <ImportantIPForm
                    client={client}
                    subnets={subnets}
                    hardware={hardware}
                    asset={editing}
                    onClose={() => { setAdding(false); setEditing(null); }}
                    onSaved={() => { setAdding(false); setEditing(null); reload(); onChanged && onChanged(); }}
                />
            )}
        </div>
    );
};

export default ImportantIPsTab;
